// AI 导演台三个智能体的快捷话术配置（唯一数据源）
// - 优化 / 创意 / 后期完全独立，禁止共用
// - 首次进入显示 firstSix（6条）；「快捷话术」浮层显示全部分组
// - 点击只填入输入框，不自动发送；不含产品名（产品切换不影响话术）

export type DirectorRole = 'optimization' | 'planning' | 'editing';

export interface QuickPromptGroup {
  title: string;
  prompts: string[];
}

export interface AgentPrompts {
  emptyTitle: string;
  emptyDesc: string;
  firstSix: string[];
  groups: QuickPromptGroup[];
}

export const DIRECTOR_PROMPTS: Record<DirectorRole, AgentPrompts> = {
  optimization: {
    emptyTitle: '今天想先看什么？',
    emptyDesc: '可以直接问账户、成本、素材表现或下一步优化动作。',
    firstSix: [
      '帮我看下现在超成本的账户',
      '哪些账户消耗高还没出单？',
      '今天这个产品整体跑得怎么样？',
      '最近跑量的特征是什么？',
      '目前最需要优先处理哪些问题？',
      '根据现在的数据，下一步应该怎么优化？',
    ],
    groups: [
      {
        title: '账户诊断',
        prompts: [
          '帮我看下现在超成本的账户',
          '哪些账户成本最高？',
          '哪些账户消耗高还没出单？',
          '哪些账户现在最需要关注？',
          '哪些账户成本正在恶化？',
        ],
      },
      {
        title: '整体表现',
        prompts: [
          '今天这个产品整体跑得怎么样？',
          '帮我总结一下当前投放情况',
          '现在整体成本怎么样？',
          '当前投放最大的几个问题是什么？',
        ],
      },
      {
        title: '素材表现',
        prompts: [
          '最近什么素材表现比较好？',
          '最近跑量的特征是什么？',
          '目前哪些素材值得继续测试？',
          '哪些素材表现开始变差？',
        ],
      },
      {
        title: '优化动作',
        prompts: [
          '目前最需要优先处理哪些问题？',
          '根据现在的数据，下一步应该怎么优化？',
          '今天优化师应该重点看什么？',
          '下一轮投放应该优先测试什么？',
        ],
      },
    ],
  },

  planning: {
    emptyTitle: '今天想做什么内容？',
    emptyDesc: '可以直接出方向、写脚本、审脚本或继续优化上一版。',
    firstSix: [
      '给我出5个新的素材方向',
      '给我写一个实拍情景剧',
      '给我写一个实拍口播',
      '给我写一个AI口播',
      '帮我审一下这个脚本',
      '下一轮创意应该做什么？',
    ],
    groups: [
      {
        title: '创意方向',
        prompts: [
          '给我出5个新的素材方向',
          '围绕这个产品再想几个新方向',
          '给我一些没做过的人群和场景',
          '围绕这个卖点出几个创意',
          '最近什么方向值得继续测试？',
        ],
      },
      {
        title: '实拍情景剧',
        prompts: [
          '给我写一个实拍情景剧',
          '给我写一个情侣情景剧',
          '给我写一个同事情景剧',
          '给我写一个家庭关系情景剧',
          '这个方向帮我展开成完整情景剧',
        ],
      },
      {
        title: '实拍口播',
        prompts: [
          '给我写一个实拍口播',
          '给我写一个真实用户口吻的口播',
          '给我写一个自拍感强一点的口播',
          '这个口播再自然一点',
          '把这个口播压成15秒',
        ],
      },
      {
        title: 'AI口播',
        prompts: [
          '给我写一个AI口播',
          '同时给我人物提示词',
          '同时给我场景提示词',
          '给我完整分镜和逐镜头提示词',
          '把这个AI口播改成15秒',
        ],
      },
      {
        title: 'AI剧情',
        prompts: [
          '给我写一个AI剧情',
          '给我设计两个人物和关系',
          '给我人物和场景提示词',
          '给我完整剧情和分镜',
          '给我逐镜头AI生成提示词',
        ],
      },
      {
        title: '脚本审核',
        prompts: [
          '帮我审一下这个脚本',
          '这个脚本哪里有问题？',
          '帮我把前三秒加强',
          '产品承接太硬了，帮我改',
          '让这个脚本更像真人表达',
          '帮我检查产品事实和合规',
        ],
      },
      {
        title: '脚本修改',
        prompts: [
          '第二个不错，继续展开',
          '换成夫妻关系',
          '冲突再强一点',
          '产品晚一点出来',
          '换成办公室场景',
          '换成25岁女性',
          '压成15秒',
          '不要这么像广告',
        ],
      },
      {
        title: '生产规划',
        prompts: [
          '最近跑量的特征是什么？',
          '下一轮创意应该做什么？',
          '明天应该重点出什么素材？',
          '下一轮应该测试哪些变量？',
        ],
      },
    ],
  },

  editing: {
    emptyTitle: '今天想先剪什么？',
    emptyDesc: '可以找混剪机会、好前贴、好后端，或直接生成剪辑方案。',
    firstSix: [
      '看看现在有没有适合混剪的素材',
      '最近有什么点击好的前贴？',
      '找几个转化好的产品后端',
      '给我一个混剪方案',
      '只换前三秒给我5个方案',
      '下一轮后期应该做什么？',
    ],
    groups: [
      {
        title: '混剪机会',
        prompts: [
          '看看现在有没有适合混剪的素材',
          '今天有什么素材值得重新组合？',
          '用点击好的前贴配转化好的后端',
          '今天后期优先混什么？',
        ],
      },
      {
        title: '好前贴',
        prompts: [
          '最近有什么点击好的前贴？',
          '给我找几个CTR好的素材',
          '哪些素材适合拿来当前贴？',
          '最近哪个素材开头值得继续测试？',
        ],
      },
      {
        title: '好后端',
        prompts: [
          '找几个转化好的产品后端',
          '哪些素材转化比较好？',
          '哪些素材适合保留产品介绍？',
          '哪些素材值得换前三秒继续测试？',
        ],
      },
      {
        title: '混剪方案',
        prompts: [
          '给我一个混剪方案',
          '用第一条前贴和第二条后端给我组合一下',
          '这个前贴配这个后端怎么剪？',
          '给我3套不同的混剪思路',
        ],
      },
      {
        title: '重剪诊断',
        prompts: [
          '这个素材怎么重新剪？',
          '这个素材为什么跑不起来？',
          '这个素材帮我出一个重剪方案',
          '这条素材还有没有重剪价值？',
        ],
      },
      {
        title: '前三秒优化',
        prompts: [
          '只换前三秒给我5个方案',
          '这个开头不够强',
          '给这个后端换几个新前贴',
          '再给我几个不同类型的Hook',
        ],
      },
      {
        title: '素材裂变',
        prompts: [
          '这个素材帮我裂变10版',
          '这个方向怎么继续做？',
          '保留后端，换5个不同前贴',
          '这个组合再给我5个变量版本',
        ],
      },
      {
        title: '表现诊断',
        prompts: [
          '这个素材点击高但是转化差怎么办？',
          '这个素材点击不高但是转化不错怎么办？',
          '哪些素材适合保留前贴？',
          '哪些素材适合保留后端？',
        ],
      },
      {
        title: '批量混剪',
        prompts: [
          '给我一个批量混剪规则',
          '我要批量混剪100条',
          '这些素材应该怎么组合？',
          '帮我整理前贴池、剧情池和后端池',
          '给我设计去重规则',
        ],
      },
      {
        title: '下一轮计划',
        prompts: [
          '下一轮后期应该做什么？',
          '今天后期重点做什么？',
          '明天后期优先处理哪些素材？',
          '根据现在的数据给我排一个后期优先级',
        ],
      },
    ],
  },
};
